import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a money market fund and how do I spot one?',
    a: 'A money market fund (MMF) is a specialized mutual fund that pools investor cash to buy ultra-short-dated, high-quality debt — U.S. Treasury bills, government repurchase agreements, commercial paper, or short-term CDs — and pays the resulting interest out as a monthly dividend. In the U.S. every mutual fund has a five-letter ticker; money market funds specifically end in a double-XX (SPAXX, VMFXX, FGXXX, SWVXX). Ordinary open-end mutual funds end in a single X (VTSAX, FXAIX). DiviDrip auto-classifies any 3-letter-prefix + XX ticker as an MMF and swaps the Insights tab over to a dedicated Money Market Fund Overview card.',
  },
  {
    q: 'Why does a money market fund target a constant $1.00 NAV?',
    a: 'A stable $1.00 net asset value is the whole point — investors treat MMF shares as a cash-equivalent, and cash doesn\'t swing in value. Retail MMFs are allowed to price their holdings at amortized cost (purchase price + smoothed accrued interest) instead of live mark-to-market, which keeps the per-share value flat. Any interest the underlying bills and repos throw off is stripped off the share price daily and paid out as a monthly dividend. The share count grows if you reinvest; the price does not.',
  },
  {
    q: 'What does "breaking the buck" mean?',
    a: 'It means the NAV falls below $1.00. The most-cited case is the Reserve Primary Fund in September 2008, which held Lehman Brothers commercial paper — when Lehman filed for bankruptcy, the fund had to write the paper down and its NAV printed at $0.97. That triggered a broader run on prime money funds and prompted a temporary U.S. Treasury backstop of the entire industry. Post-2014 and 2023 SEC reforms tightened the rules further: institutional prime funds now use a floating NAV and can impose liquidity fees or redemption gates during stress; retail government and Treasury funds kept the stable $1.00 NAV format.',
  },
  {
    q: 'How does the yield work if the price never moves?',
    a: 'Every business day, the interest thrown off by the underlying paper is calculated and accrued. Instead of letting that accrued interest push the NAV to $1.001, the fund books it as a dividend receivable and resets the share price back to exactly $1.00. On the last business day of the month those accruals are paid out — either as cash into your brokerage sweep or as fractional new shares if you DRIP. The public quote is the fund\'s 7-day yield: the past week\'s daily accruals, annualized, minus the expense ratio. DiviDrip shows the 7-day yield and total net assets on the MMF Overview card at the top of the Insights tab.',
  },
  {
    q: 'Who actually holds MMFs and why?',
    a: 'Four groups use them heavily: (1) retail investors parking cash they want to keep liquid but not lose to inflation drag — the classic emergency-fund or "waiting for a market pullback" bucket; (2) corporate treasuries holding operational cash for payroll and payables; (3) endowments, pension funds, and institutional cash managers with tens of billions of short-duration cash; (4) brokerage firms themselves, who use retail-class MMFs (VMFXX, SPAXX, FGXXX, SWVXX) as the automatic cash-sweep destination for uninvested account balances. That last use case is why an ordinary trading account tends to hold one of these tickers by default even if you never explicitly bought it.',
  },
  {
    q: 'Are the dividends taxed as qualified dividends?',
    a: 'No — MMF dividends are almost always ordinary (non-qualified) income. That means they\'re taxed at your marginal federal rate (10-37% in 2026), not the 0/15/20% qualified-dividend rate. The IRS treats the payouts as interest even though brokers report them on Form 1099-DIV, because the underlying is short-term debt, not corporate equity. Reinvesting via DRIP does not defer the tax — the reinvested amount still counts as income for the year it was distributed. Exceptions: Treasury MMFs are exempt from state and local tax on the portion attributable to direct U.S. Treasury obligations, and municipal MMFs are federally tax-exempt (single-state MMFs can also be state-exempt for residents of that state).',
  },
  {
    q: 'Are MMFs okay inside a Roth IRA?',
    a: 'From a tax standpoint, yes — a Roth IRA neutralizes the ordinary-income tax problem entirely. Everything the MMF earns compounds tax-free and comes out tax-free at qualified withdrawal (age 59½ and 5-year rule). Two practical uses inside a Roth: (1) a "dry powder" sleeve — cash waiting for a market drawdown to deploy into stocks while still earning the short-rate yield; (2) a stable-value fallback for Roth contributions you might need to pull back out (contributions can be withdrawn tax- and penalty-free any time, and a $1.00 NAV product means you won\'t lose principal if that day comes). The one real downside: an MMF barely beats inflation over long horizons, so parking your entire Roth in one wastes the account\'s biggest advantage — decades of tax-free compound growth on higher-return assets.',
  },
  {
    q: 'Is the $1.00 NAV guaranteed?',
    a: 'No. MMFs are governed by SEC Rule 2a-7 and use amortized-cost accounting to keep the NAV stable, but "aims to maintain" is not the same as "guaranteed." Retail government and Treasury MMFs have essentially never broken the buck; the Reserve Primary Fund in 2008 (prime, holding Lehman paper) is the widely-cited counterexample. MMFs are also not FDIC-insured — SIPC covers your brokerage custody up to $500K, but that\'s a custody protection, not credit-loss coverage. Read the prospectus before treating any specific MMF as a true cash-equivalent.',
  },
  {
    q: 'How does DiviDrip handle MMFs differently from a stock or ETF?',
    a: 'The MMF classifier fires on any ticker matching the strict pattern (3 letters + XX). When it does: (1) the Insights tab gets a Money Market Fund Overview card with the 7-day yield and total net assets taken from the fund\'s monthly SEC Form N-MFP filing (a market data vendor is the fallback); (2) the Dividend Triangle tab is hidden — the Revenue × EPS × Dividend Growth model doesn\'t apply to a constant-NAV product; (3) the DDM Valuation Matrix (target buy price) is replaced with an "N/A — Money Market Fund" note; (4) the SEO page renders a dedicated MMF section with the yield and AUM as headline metrics; (5) the Div → Non-Div admin reconcile panel excludes MMFs so they don\'t flood the queue.',
  },
];

const EXAMPLES = [
  { ticker: 'SPAXX',  family: 'Fidelity',       kind: 'Government MMF', use: 'Default cash-sweep in Fidelity brokerage accounts' },
  { ticker: 'VMFXX',  family: 'Vanguard',       kind: 'Government MMF', use: 'Default settlement fund in Vanguard brokerage accounts' },
  { ticker: 'VUSXX',  family: 'Vanguard',       kind: 'Treasury MMF',   use: '≈100% direct Treasuries — best for high-tax states (CA, NY, NJ) chasing state-tax exemption on cash yield' },
  { ticker: 'SWVXX',  family: 'Schwab',         kind: 'Prime MMF',      use: 'Schwab\u2019s retail prime option \u2014 holds commercial paper + CDs, accepts a sliver of extra credit risk for a little more yield' },
  { ticker: 'FGXXX',  family: 'First American', kind: 'Government MMF', use: 'Institutional Class X — often used as underlying cash-sweep by advisors and thematic ETFs' },
  { ticker: 'VMSXX',  family: 'Vanguard',       kind: 'Municipal MMF',  use: 'Federally tax-exempt — best for high-income investors in a taxable account' },
];

export default function LearnMoneyMarketFunds() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Money Market Funds — the XX-ticker guide | DiviDrip"
      description="Everything about US money market funds — the 5-letter XX ticker convention, constant $1.00 NAV mechanics, how yield gets paid without moving the price, tax treatment, Roth IRA use, and how DiviDrip classifies and displays them."
      canonical="https://twylightcrow.com/learn/money-market-funds"
      guidePath="/learn/money-market-funds"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Cash-equivalents & sweep vehicles"
      h1="Money Market Funds &mdash; the 5-Letter XX Ticker Guide"
      datePublished="2026-08-30"
      jsonLd={jsonLd}
      related={[
        { to: '/money-market-fund-yields', label: 'Money market fund yields — every fund ranked by 7-day SEC yield' },
        { to: '/learn/qualified-dividends', label: 'Qualified vs ordinary dividends' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP is most powerful inside a Roth IRA' },
        { to: '/learn/daily-dividend-stocks', label: 'Daily dividend stocks (SATA)' },
        { to: '/learn/cost-basis-methods', label: 'Cost basis methods (FIFO vs Average)' },
        { to: '/screener', label: 'Stock Screener' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        If you have ever looked at a brokerage account and seen a five-letter
        ticker ending in <strong>XX</strong> — <Link to="/stock/SPAXX">SPAXX</Link>,{' '}
        <Link to="/stock/VMFXX">VMFXX</Link>, <Link to="/stock/FGXXX">FGXXX</Link>,{' '}
        <Link to="/stock/SWVXX">SWVXX</Link> — you were looking at a
        <em> money market fund</em>. That specific ticker shape is a signalling
        convention: single-X endings mark ordinary open-end mutual funds
        (VTSAX, FXAIX), and the double-XX marks the money-market subclass.
        DiviDrip uses that shape as its primary classifier — every 3-letter-
        prefix + XX ticker is auto-flagged, and the app swaps in an MMF-
        specific Insights card, hides the Dividend Triangle, and hides the
        DDM Valuation Matrix on those pages.
      </p>

      <h2>A quick history</h2>
      <p>
        Money market funds were invented in <strong>1971</strong> by Bruce Bent
        and Henry B. R. Brown, who launched <em>The Reserve Fund</em> to
        route ordinary savers around Regulation Q — the Federal Reserve rule
        that capped the interest rate banks were allowed to pay on deposits.
        By pooling investor cash and buying short-term corporate and government
        debt, they could pass the higher market yields directly through to
        individual investors. The idea took off during the high-inflation
        1970s and by the 1980s an MMF was the default cash sleeve inside
        every major brokerage.
      </p>
      <p>
        The next big moment was <strong>September 16, 2008</strong>. The
        Reserve Primary Fund — a direct descendant of Bent and Brown&apos;s
        original fund — held commercial paper issued by Lehman Brothers.
        When Lehman filed for bankruptcy the day before, the Reserve
        Primary&apos;s NAV printed at <strong>$0.97</strong>, breaking the
        buck for the first time in decades. That triggered a run on the
        broader money market industry and prompted the U.S. Treasury to
        temporarily guarantee the entire sector. The event drove two waves
        of SEC reform (2014 and 2023): institutional prime funds now use a
        floating NAV and can impose liquidity fees or redemption gates;
        retail government and Treasury funds kept the stable $1.00 NAV.
      </p>

      <h2>How the $1.00 NAV works</h2>
      <p>
        The defining feature of a retail MMF is the constant $1.00 share
        price. The mechanism has two moving parts:
      </p>
      <ol>
        <li>
          <strong>Amortized-cost accounting.</strong> SEC Rule 2a-7 allows
          MMFs to value their holdings at purchase price plus smoothly-accrued
          interest, rather than fluctuating live market price. Because the
          holdings are short-duration (portfolio maturity typically 30–60
          days), the gap between amortized cost and true mark-to-market
          stays negligible under normal conditions.
        </li>
        <li>
          <strong>Daily interest sweep.</strong> Whatever interest the
          underlying bills, repos, and commercial paper throw off during a
          given day is booked as a dividend receivable, not as an increase
          in NAV. Instead of the share price drifting from $1.00 to $1.001
          to $1.002, the price resets to exactly $1.00 every close and the
          accrued interest queues up to be paid out at month-end.
        </li>
      </ol>
      <p>
        You can think of the NAV like a water level held perfectly flat by
        draining every drop of new interest into a bucket on the side. The
        bucket is your dividend; the water level never moves.
      </p>

      <h2>What&apos;s inside an MMF</h2>
      <p>
        MMFs are legally restricted to short-duration, high-credit-quality
        debt. The exact mix depends on the fund category:
      </p>
      <ul>
        <li><strong>U.S. Treasury bills</strong> — direct short-dated
          obligations of the U.S. Treasury. The gold standard for safety.</li>
        <li><strong>Repurchase agreements</strong> (&ldquo;repos&rdquo;) —
          overnight loans collateralized by Treasuries or agency paper.</li>
        <li><strong>Agency paper</strong> — short-duration debt from
          Federal Home Loan Banks and similar government-sponsored entities.</li>
        <li><strong>Commercial paper</strong> — short-term unsecured notes
          from investment-grade corporations. Only prime MMFs hold this.</li>
        <li><strong>Negotiable CDs</strong> — large-denomination bank
          certificates of deposit maturing in under 397 days.</li>
        <li><strong>Municipal notes</strong> — short-term paper from state and
          local governments. Only muni MMFs hold this.</li>
      </ul>

      <h2>The four categories</h2>
      <p>
        The tax treatment and the risk profile both depend on what a specific
        MMF holds. In practice retail investors are picking between four
        categories:
      </p>
      <ul>
        <li>
          <strong>Government MMFs</strong> — hold at least 99.5% Treasuries,
          agency paper, and repos backed by those. Examples:{' '}
          <Link to="/stock/SPAXX">SPAXX</Link>,{' '}
          <Link to="/stock/VMFXX">VMFXX</Link>,{' '}
          <Link to="/stock/FGXXX">FGXXX</Link>,{' '}
          <Link to="/stock/SNVXX">SNVXX</Link>. The safest option. Distributions
          are federally taxable at ordinary rates; state and local tax
          treatment depends on the fraction attributable to direct U.S.
          Treasury obligations.
        </li>
        <li>
          <strong>Treasury MMFs</strong> — hold ≈100% direct U.S. Treasuries.
          Examples: <Link to="/stock/VUSXX">VUSXX</Link>,{' '}
          <Link to="/stock/SNSXX">SNSXX</Link>. Distributions attributable to
          direct Treasuries are <em>exempt from state and local income tax</em> —
          which makes them meaningfully better than government MMFs for
          residents of high-tax states like California, New York, or New Jersey.
        </li>
        <li>
          <strong>Prime MMFs</strong> — hold a mix of government paper and
          investment-grade commercial paper / CDs. Examples:{' '}
          <Link to="/stock/SWVXX">SWVXX</Link>,{' '}
          <Link to="/stock/SPRXX">SPRXX</Link>. Slightly higher yield in
          exchange for a sliver of extra credit risk (and the small chance
          of a liquidity fee or gate during severe market stress under
          post-2023 SEC rules).
        </li>
        <li>
          <strong>Municipal MMFs</strong> — hold short-duration muni paper.
          Examples: <Link to="/stock/VMSXX">VMSXX</Link>,{' '}
          <Link to="/stock/SWTXX">SWTXX</Link>. Distributions are federally
          tax-exempt; single-state muni MMFs (like{' '}
          <Link to="/stock/SWKXX">SWKXX</Link> or{' '}
          <Link to="/stock/VCTXX">VCTXX</Link> for California residents) are
          also state-exempt. Yields quote lower than taxable
          MMFs but the tax-equivalent yield can beat them for high-income
          investors in a standard taxable brokerage account —{' '}
          <Link to="/money-market-fund-yields">the yields page has a
          tax-equivalent calculator</Link> for exactly that comparison.
        </li>
      </ul>

      <h2>Yields, dividends, and the tax hit</h2>
      <p>
        The public yield for an MMF is the <strong>7-day SEC yield</strong>:
        the past week&apos;s daily accruals annualized and net of expense
        ratio. Every US money fund reports it to the SEC monthly on{' '}
        <strong>Form N-MFP</strong>, share class by share class, and that
        filing is what DiviDrip reads — a market data vendor is only the
        fallback. It shows on the MMF Overview card alongside total net
        assets, and{' '}
        <Link to="/money-market-fund-yields">the money market fund yields
        page</Link> ranks every fund we track on that same measure, split by
        the four categories above.
      </p>
      <p>
        Distributions are paid monthly on the last business day. They land
        as cash in your brokerage sweep or reinvest into fractional shares
        if you have DRIP turned on for the ticker. Either way, the tax
        treatment is the same and the tax treatment is the biggest gotcha
        for beginners:
      </p>
      <ul>
        <li>
          MMF dividends are <strong>ordinary (non-qualified) income</strong> —
          taxed at your marginal federal rate (10–37% in 2026). Not the
          friendlier 0/15/20% qualified-dividend rate that applies to most
          common-stock dividends.
        </li>
        <li>
          Even though the broker reports them on <strong>Form 1099-DIV</strong> (Box 1a,
          Ordinary Dividends), the IRS treats the payouts as interest — because
          the underlying is short-term debt, not corporate equity.
        </li>
        <li>
          <strong>Reinvestment does not defer the tax.</strong> If DRIP is on
          and you never touch the cash, you still owe ordinary income tax on
          every distribution in the year it&apos;s paid. That&apos;s the
          &ldquo;reinvestment tax trap&rdquo; — you can wind up owing tax on
          money you have to raise from other sources.
        </li>
        <li>
          <strong>State-tax carve-outs.</strong> Treasury MMFs are state-tax-
          exempt on the direct-Treasury portion. Municipal MMFs are federally
          tax-exempt. Single-state muni MMFs are state-exempt for residents
          of that state.
        </li>
      </ul>
      <h2>The expense ratio is the whole ballgame</h2>
      <p>
        Money funds all buy the same short-dated paper, so what separates
        their yields is mostly the fee. Across the funds we track, the ones
        charging 0.20% or less pay a median 7-day yield roughly{' '}
        <strong>60&ndash;70 basis points higher</strong> than the ones charging
        0.50% or more &mdash; on $50,000 of cash that is a few hundred dollars
        a year for holding a different ticker at the same broker. Two things
        follow: buy the cheapest share class you can actually access, and
        don&apos;t subtract the expense ratio from the quoted yield. The 7-day
        SEC yield is <em>already</em> net of fees, so the fee explains the
        yield rather than reducing it a second time. We read each fund&apos;s
        expense ratio from its SEC <strong>N-CSR</strong> shareholder report
        and show it beside the yield on{' '}
        <Link to="/money-market-fund-yields">the yields page</Link>.
      </p>

      <h2>Money market ETFs are a different product</h2>
      <p>
        A handful of exchange-traded money market funds now exist &mdash;
        GMMF, PMMF, JMMF, MMKT, SBIL. They are not 5-letter XX tickers and
        DiviDrip does not classify them as MMFs, correctly: they trade
        intraday at a floating market price near $100 a share, no broker
        sweeps cash into them, and they quote a trailing distribution yield
        rather than a 7-day SEC yield. They appear in their own section of
        the yields page, deliberately apart from the $1.00-NAV funds, because
        ranking the two yield types together compares a backward-looking
        number with a forward-looking one. You can see them, and how their
        distribution yields currently compare, on{' '}
        <Link to="/money-market-fund-yields">the money market fund yields
        page</Link>.
      </p>



      <h2>Why a Roth IRA is the tidiest wrapper</h2>
      <p>
        The ordinary-income tax hit disappears entirely inside a Roth. Every
        monthly distribution compounds tax-free, and qualified withdrawals
        (age 59&frac12; plus the 5-year rule) come out tax-free. Two use
        cases where an MMF-inside-a-Roth pattern shows up a lot:
      </p>
      <ul>
        <li>
          <strong>Dry powder.</strong> Contribute your annual $7,500 (or
          $8,600 if you&apos;re 50+) at the start of the year, park it in a
          government MMF, and deploy into stocks when the market gives you
          an entry point you like. You collect the prevailing short-rate
          yield in the meantime — see{' '}
          <Link to="/money-market-fund-yields">the current 7-day yields</Link>.
        </li>
        <li>
          <strong>Backup emergency sleeve.</strong> Roth contributions can be
          withdrawn tax- and penalty-free any time. Keeping them in a $1.00
          NAV MMF means the balance is always there at face value if you
          ever need to pull it out.
        </li>
      </ul>
      <p>
        The main tradeoff: an MMF paying the short rate is unlikely to beat
        inflation over long horizons, and a Roth IRA&apos;s biggest advantage is
        decades of
        tax-free compound growth on higher-return assets. Using the whole
        account for cash wastes that advantage. Most professionals treat an
        MMF sleeve as a &lt;20% allocation, not the whole thing.
      </p>

      <h2>Examples you&apos;ll actually see in a brokerage account</h2>
      <p>
        The following are all DiviDrip-classified MMFs (5-letter, XX suffix,
        confirmed <code>quoteType: MONEYMARKET</code>):
      </p>
      <ul>
        {EXAMPLES.map((row) => (
          <li key={row.ticker}>
            <Link to={`/stock/${row.ticker}`}><strong>{row.ticker}</strong></Link>{' '}
            ({row.family}, {row.kind}) — {row.use}.
          </li>
        ))}
      </ul>

      <h2>How DiviDrip classifies and displays them</h2>
      <p>
        On every 5-letter XX ticker, DiviDrip:
      </p>
      <ul>
        <li>Stamps <code>is_money_market_fund: true</code> on the cache doc,
          plus <code>mmf_family</code> (Fidelity / Schwab / Vanguard / etc.),
          the live <code>mmf_7day_yield</code>, and total <code>mmf_aum</code>.</li>
        <li>Renders a <strong>Money Market Fund Overview</strong> card at the
          top of the Insights tab, showing the 7-day yield and total net
          assets as headline metric tiles.</li>
        <li>Hides the <strong>Dividend Triangle</strong> tab — Revenue × EPS
          × Dividend Growth doesn&apos;t apply to a constant-NAV product.</li>
        <li>Replaces the <strong>DDM Valuation Matrix</strong> and Target Buy
          Price with a small &ldquo;N/A — Money Market Fund&rdquo; note.</li>
        <li>Serves a dedicated <strong>MMF SEO section</strong> on the public
          stock page so search users landing on FGXXX or SPAXX see the
          $1.00-NAV explainer + risk note before the standard dividend body.</li>
        <li>Excludes MMFs from the <em>Div → Non-Div</em> admin reconcile
          panel — MMFs use <code>mmf_7day_yield</code> on a separate field,
          not <code>dividend_yield</code>, so they don&apos;t belong in that
          reconciliation queue.</li>
      </ul>

      <h2>The bottom line</h2>
      <p>
        Money market funds are the app&apos;s designated <em>cash tier</em> —
        useful for parking money you can&apos;t afford to lose to volatility,
        but not a wealth-building engine. Treat them like your brokerage&apos;s
        version of a checking account with a yield: safe, liquid, and
        earning short-rate interest, but never expected to appreciate. And
        keep them out of a standard taxable account when possible — the
        ordinary-income tax hit is the single biggest thing separating
        an MMF from an equally-yielding tax-favored alternative.
      </p>

      <p className="italic text-sm text-muted-foreground mt-8">
        Not investment, tax, or legal advice. MMFs aim to maintain a constant
        $1.00 NAV but are not guaranteed to do so and are not FDIC-insured.
        Read the individual fund&apos;s prospectus before treating it as a
        cash-equivalent. Tax rules cited reflect 2026 U.S. federal law and
        can change; consult a tax professional for your specific situation.
      </p>
    </LearnPage>
  );
}
